import { useEffect, useState } from 'react'
import { cubicBezier, LayoutGroup, motion } from 'framer-motion'

import Image from '@/components/ui/image'
import Lottie from '@/components/lottie'
import RichText from '@/components/ui/rich-text'
import CtaLink from '@/components/ui/cta-link'

import ArrowDown from '@/icons/bwi/angle-down.svg'

import { cx } from '@/utils/strings'
import {
  TypeComponentAccordionCard,
  TypeSectionAccordionCards,
  isTypeComponentLottie,
  isTypeResourceImage,
} from '@/types/ctf'
import { DotLottie } from '@lottiefiles/dotlottie-react'

type AccordionCardsProps = TypeSectionAccordionCards<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']

function AnimatedSpacer({ height }: { height: number }) {
  return (
    <motion.div
      variants={{
        active: {
          height,
        },
        inactive: {
          height: 0,
        },
      }}
    />
  )
}

export default function AccordionCards({
  title,
  subtext,
  description,
  imageWidth,
  imagePosition = 'Right',
  cards,
}: AccordionCardsProps) {
  const [active, setActive] = useState<TypeComponentAccordionCard<'WITHOUT_UNRESOLVABLE_LINKS'> | null | undefined>(
    cards ? cards[0] : null
  )
  const [lottieCardDimensions, setLottieCardDimensions] = useState<Record<string, { width: number; height: number }>>(
    {}
  )

  // Used to conditionally render the image/lottie on mobile
  // - Loading lottie conmponents hidden with CSS causes issues
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleLottieLoad = (ref: DotLottie | null, cardId: string) => {
    if (ref) {
      const animationSize = ref.animationSize()
      setLottieCardDimensions((prev) => ({ ...prev, [cardId]: animationSize }))
    }
  }

  return (
    <div className={cx('px-6 md:px-16 lg:px-12 py-20 bg-secondary-50 dark:bg-transparent')}>
      {subtext && <p className="text-eyebrow uppercase text-center mb-3">{subtext}</p>}
      {title && (
        <div className="max-w-4xl lg:max-w-6xl mx-auto text-secondary-900 px-5 mb-12 dark:text-white">
          <h3 className="h2 text-center">{title}</h3>
          {description && (
            <div className="text-center">
              <RichText body={description} />
            </div>
          )}
        </div>
      )}
      <div className={cx('max-w-8xl mx-auto flex gap-8 items-center justify-center')}>
        <div className={cx('flex-1 flex flex-col items-center justify-center')}>
          <LayoutGroup>
            {cards &&
              cards.map((card) => {
                const isActive = active?.sys.id === card?.sys.id
                return (
                  <motion.button
                    key={card?.sys.id}
                    type="button"
                    className={cx(
                      'w-full',
                      isActive && 'bg-secondary-50 dark:bg-gray-950 mt-[-1px] z-10 mb-1',
                      !isActive && ' transition-colors border-b border-secondary-100 dark:border-secondary-700'
                    )}
                    layout="position"
                    transition={{
                      duration: 0.3,
                      ease: cubicBezier(0.25, 1, 0.5, 1),
                    }}
                    initial="inactive"
                    animate={isActive ? 'active' : 'inactive'}
                    onClick={(e) => {
                      if (!isActive) {
                        setActive(card)
                        if (isMobile) {
                          const button = e.currentTarget
                          setTimeout(() => {
                            window?.scrollTo({
                              top: button.offsetTop - 100,
                              behavior: 'smooth',
                            })
                          }, 100)
                        }
                      }
                    }}
                  >
                    <div
                      className={cx(
                        'relative flex-1 w-full text-left will-change-contents px-6 py-3',
                        isActive &&
                          'cursor-auto select-text rounded-lg bg-white dark:bg-secondary-800 border-b-2 dark:border-secondary-700 border-secondary-100'
                      )}
                    >
                      <div className="absolute top-5 right-6 z-1">
                        <motion.div
                          className="w-4 h-4"
                          variants={{
                            active: {
                              rotate: 180,
                            },
                            inactive: {
                              rotate: 0,
                            },
                          }}
                        >
                          <ArrowDown className="text-secondary-900 dark:text-secondary-50" />
                        </motion.div>
                      </div>

                      <AnimatedSpacer height={10} />
                      {card?.fields?.subtext && (
                        <span className="text-eyebrow uppercase block mb-3">{card.fields.subtext}</span>
                      )}
                      {card?.fields?.title && (
                        <h4 className={cx(!isActive && 'text-primaryBlue mb-0', isActive && 'mb-5 lg:mb-0', 'h6')}>
                          {card.fields.title}
                        </h4>
                      )}
                      {isActive && isMobile && card?.fields?.image && (
                        <div
                          className="[&_img]:rounded-2xl [&_figure]:my-0 mb-6"
                          style={
                            lottieCardDimensions[card.sys.id]
                              ? {
                                  aspectRatio: `${lottieCardDimensions[card.sys.id].width} / ${lottieCardDimensions[card.sys.id].height}`,
                                }
                              : {
                                  aspectRatio: '16 / 9',
                                }
                          }
                        >
                          {active?.fields?.image && isTypeResourceImage(active.fields.image) && (
                            <Image {...active.fields.image.fields} />
                          )}
                          {active?.fields?.image && isTypeComponentLottie(active.fields.image) && (
                            <Lottie {...active.fields.image.fields} />
                          )}
                        </div>
                      )}

                      {card?.fields?.description && (
                        <>
                          <AnimatedSpacer height={12} />
                          <motion.div
                            className="[&_p]:text-base lg:[&_p]:text-lg [&_p]:mb-0 overflow-clip"
                            variants={{
                              active: {
                                opacity: 1,
                                y: 0,
                                height: 'auto',
                              },
                              inactive: {
                                opacity: 0,
                                y: 10,
                                height: 0,
                              },
                            }}
                            transition={isMobile ? { duration: 0 } : undefined}
                          >
                            <div className="pr-5">
                              <RichText body={card.fields.description} />
                            </div>
                            {card?.fields?.ctas && card?.fields?.ctas.length > 0 && (
                              <div className="mt-4 flex items-start flex-wrap gap-2">
                                {card?.fields?.ctas?.map((cta) => (
                                  <CtaLink
                                    key={cta?.sys.id}
                                    {...cta?.fields}
                                    to={cta?.fields.link?.fields.url}
                                    className="cursor-pointer"
                                    size="Smaller"
                                    tabIndex={isActive ? 0 : -1}
                                  >
                                    {cta?.fields.label}
                                  </CtaLink>
                                ))}
                              </div>
                            )}
                          </motion.div>
                        </>
                      )}
                      <AnimatedSpacer height={12} />
                    </div>
                  </motion.button>
                )
              })}
          </LayoutGroup>
        </div>
        <div
          className={cx(
            'hidden lg:grid place-items-center h-full',
            !imagePosition || (imagePosition === 'Left' && '-order-1'),
            !imageWidth || (imageWidth === '2/3' && 'md:w-2/3'),
            imageWidth === '1/2' && 'md:w-1/2',
            imageWidth === '1/3' && 'md:w-1/3'
          )}
          style={{
            gridTemplateAreas: `"stack"`,
          }}
        >
          {!isMobile &&
            cards?.map((card) => {
              const isActive = active?.sys.id === card?.sys.id
              const animationSize = card ? lottieCardDimensions[card.sys.id] : null
              return (
                <motion.div
                  key={card?.sys.id}
                  className="[&_img]:rounded-2xl col-span-1"
                  animate={isActive ? 'active' : 'inactive'}
                  variants={{
                    active: { opacity: 1, y: 0, zIndex: 2 },
                    inactive: {
                      opacity: 0,
                      y: 10,
                      zIndex: 1,
                      transition: { duration: 0.25, ease: cubicBezier(0.25, 1, 0.5, 1) },
                    },
                  }}
                  initial={false}
                  transition={{ duration: 0.5, ease: cubicBezier(0.25, 1, 0.5, 1) }}
                  style={{
                    gridArea: 'stack',
                    aspectRatio: animationSize ? `${animationSize.width} / ${animationSize.height}` : undefined,
                  }}
                >
                  {card?.fields?.image && isTypeResourceImage(card.fields.image) && (
                    <Image {...card.fields.image.fields} />
                  )}
                  {card?.fields?.image && isTypeComponentLottie(card.fields.image) && (
                    <Lottie
                      {...card.fields.image.fields}
                      autoplayType={isActive ? card.fields.image.fields.autoplayType : 'none'}
                      onLoad={(ref) => handleLottieLoad(ref, card.sys.id)}
                    />
                  )}
                </motion.div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
